import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { CheckCircleIcon, ClockIcon, FileTextIcon, ArrowRightIcon, BuildingIcon, CalendarIcon, CheckIcon, XCircleIcon, DownloadIcon, MessageSquareIcon, SendIcon, PaperclipIcon, ChevronLeftIcon, InfoIcon, BookOpenIcon } from 'lucide-react';
const ExemptionDetail = () => {
  const {
    requestId
  } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  // Fetch exemption request details
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      // Sample data for the exemption request
      const exemptionRequest = {
        id: requestId,
        programName: 'Master of Business Administration',
        university: 'UAE Business School',
        fieldOfStudy: 'Business Administration',
        status: 'In Review',
        currentStep: 'MoHESR',
        submittedDate: 'May 5, 2024',
        lastUpdated: '3 days ago',
        justification: 'I am currently working in a management position at a multinational company and need to enhance my business administration skills to advance in my career. This program offers specialized courses in international business that are not available in priority fields. My employer is supporting my application as it aligns with their leadership development program.',
        documents: [{
          id: 'doc1',
          name: 'Admission Letter.pdf',
          type: 'PDF',
          uploadDate: 'May 5, 2024'
        }, {
          id: 'doc2',
          name: 'Program Curriculum.pdf',
          type: 'PDF',
          uploadDate: 'May 5, 2024'
        }, {
          id: 'doc3',
          name: 'CV.pdf',
          type: 'PDF',
          uploadDate: 'May 5, 2024'
        }, {
          id: 'doc4',
          name: 'Employer Support Letter.pdf',
          type: 'PDF',
          uploadDate: 'May 5, 2024'
        }]
      };
      // Sample messages
      const sampleMessages = [{
        id: 1,
        sender: 'system',
        content: 'Your exemption request has been received and is now being reviewed by the Ministry of Higher Education and Scientific Research (MoHESR).',
        timestamp: 'May 5, 2024 • 10:30 AM'
      }, {
        id: 2,
        sender: 'MoHESR',
        content: 'We are reviewing your exemption request. Could you please provide more information about how this program aligns with your current job responsibilities?',
        timestamp: 'May 7, 2024 • 2:15 PM'
      }, {
        id: 3,
        sender: 'student',
        content: 'Thank you for your message. In my current role as a Project Manager, I oversee international teams and manage cross-border projects. The MBA program will provide me with advanced knowledge in global business strategies, financial management, and leadership skills that are directly applicable to my work. My employer has identified this specific program as part of my career development plan.',
        timestamp: 'May 7, 2024 • 4:45 PM'
      }, {
        id: 4,
        sender: 'MoHESR',
        content: 'Thank you for the additional information. We have forwarded your request to the Executive Human Capability Development Council (EHCDC) for final review.',
        timestamp: 'May 9, 2024 • 11:20 AM'
      }];
      setRequest(exemptionRequest);
      setMessages(sampleMessages);
      setLoading(false);
    }, 1000);
  }, [requestId]);
  const handleSendMessage = e => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    const newMsg = {
      id: messages.length + 1,
      sender: 'student',
      content: newMessage,
      timestamp: new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };
    setMessages([...messages, newMsg]);
    setNewMessage('');
  };
  // Get the step number based on current step
  const getStepNumber = step => {
    switch (step) {
      case 'Submitted':
        return 1;
      case 'MoHESR':
        return 2;
      case 'EHCDC':
        return 3;
      case 'Completed':
        return 4;
      default:
        return 1;
    }
  };
  const getStatusColor = status => {
    switch (status) {
      case 'Submitted':
        return 'bg-blue-50 text-blue-700';
      case 'In Review':
        return 'bg-purple-50 text-purple-700';
      case 'Approved':
        return 'bg-green-50 text-green-700';
      case 'Rejected':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };
  const getStatusIcon = status => {
    switch (status) {
      case 'Submitted':
        return <FileTextIcon className="w-4 h-4 mr-1.5" data-id="element-3859" />;
      case 'In Review':
        return <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-3860" />;
      case 'Approved':
        return <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3861" />;
      case 'Rejected':
        return <XCircleIcon className="w-4 h-4 mr-1.5" data-id="element-3862" />;
      default:
        return <FileTextIcon className="w-4 h-4 mr-1.5" data-id="element-3863" />;
    }
  };
  if (loading) {
    return <Layout title="Exemption Request" persona="Student" data-id="element-3864">
        <div className="max-w-5xl mx-auto" data-id="element-3865">
          <div className="text-center py-12" data-id="element-3866">
            <ClockIcon className="w-12 h-12 text-gray-300 mx-auto animate-pulse" data-id="element-3867" />
            <p className="mt-4 text-gray-500" data-id="element-3868">Loading request details...</p>
          </div>
        </div>
      </Layout>;
  }
  if (!request) {
    return <Layout title="Exemption Request" persona="Student" data-id="element-3869">
        <div className="max-w-5xl mx-auto" data-id="element-3870">
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200 shadow-sm" data-id="element-3871">
            <XCircleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" data-id="element-3872" />
            <h3 className="text-xl font-medium text-gray-700 mb-2" data-id="element-3873">
              Request Not Found
            </h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6" data-id="element-3874">
              The exemption request you're looking for doesn't exist or you
              don't have permission to view it.
            </p>
            <button onClick={() => navigate('/student/applications-and-scholarships?tab=exemptions')} className="h-10 px-4 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors" data-id="element-3875">
              Back to Exemptions
            </button>
          </div>
        </div>
      </Layout>;
  }
  const currentStepNumber = getStepNumber(request.currentStep);
  return <Layout title="Exemption Request" persona="Student" data-id="element-3876">
      <div className="max-w-5xl mx-auto" data-id="element-3877">
        {/* Back button */}
        <button onClick={() => navigate('/student/applications-and-scholarships?tab=exemptions')} className="mb-6 flex items-center text-sm text-gray-600 hover:text-gray-900" data-id="element-3878">
          <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3879" />
          Back to Exemptions
        </button>
        {/* Header with status */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6" data-id="element-3880">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-3881">
            <div data-id="element-3882">
              <h1 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-3883">
                Exemption Request: {request.programName}
              </h1>
              <p className="text-gray-600 flex items-center" data-id="element-3884">
                <BuildingIcon className="w-4 h-4 mr-1.5" data-id="element-3885" />
                {request.university}
              </p>
            </div>
            <div className="mt-4 md:mt-0" data-id="element-3886">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center ${getStatusColor(request.status)}`} data-id="element-3887">
                {getStatusIcon(request.status)}
                {request.status}
              </span>
            </div>
          </div>
          {/* Timeline */}
          <div className="mb-6" data-id="element-3888">
            <div className="flex items-center justify-between mb-2" data-id="element-3889">
              {['Submitted', 'MoHESR Review', 'EHCDC Review', 'Decision'].map((step, index) => <div key={index} className="flex flex-col items-center" data-id="element-3890">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index + 1 <= currentStepNumber ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-3891">
                      {index + 1 <= currentStepNumber ? <CheckIcon className="w-5 h-5" data-id="element-3892" /> : <span className="text-xs" data-id="element-3893">{index + 1}</span>}
                    </div>
                    <span className={`text-xs mt-1 ${index + 1 <= currentStepNumber ? 'text-primary-600 font-medium' : 'text-gray-500'}`} data-id="element-3894">
                      {step}
                    </span>
                  </div>)}
            </div>
            <div className="relative h-2 bg-gray-200 rounded-full mt-1" data-id="element-3895">
              <div className="absolute h-2 bg-primary-500 rounded-full" style={{
              width: `${currentStepNumber / 4 * 100}%`
            }} data-id="element-3896"></div>
            </div>
          </div>
          {/* Important dates */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4" data-id="element-3897">
            <div data-id="element-3898">
              <p className="text-xs text-gray-500 mb-1" data-id="element-3899">Submitted</p>
              <p className="text-sm font-medium text-gray-800" data-id="element-3900">
                {request.submittedDate}
              </p>
            </div>
            <div data-id="element-3901">
              <p className="text-xs text-gray-500 mb-1" data-id="element-3902">Last Updated</p>
              <p className="text-sm font-medium text-gray-800" data-id="element-3903">
                {request.lastUpdated}
              </p>
            </div>
            <div data-id="element-3904">
              <p className="text-xs text-gray-500 mb-1" data-id="element-3905">Current Stage</p>
              <p className="text-sm font-medium text-gray-800" data-id="element-3906">
                {request.currentStep} Review
              </p>
            </div>
          </div>
        </div>
        {/* Two-column layout for details and messages */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3907">
          {/* Left column - Request details */}
          <div className="lg:col-span-1 space-y-6" data-id="element-3908">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3909">
              <div className="p-4 border-b border-gray-100 bg-gray-50" data-id="element-3910">
                <h2 className="text-lg font-semibold text-gray-800" data-id="element-3911">
                  Request Details
                </h2>
              </div>
              <div className="p-4" data-id="element-3912">
                <div className="space-y-4" data-id="element-3913">
                  <div data-id="element-3914">
                    <h3 className="text-sm font-medium text-gray-700 mb-1" data-id="element-3915">
                      Program
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-3916">
                      {request.programName}
                    </p>
                  </div>
                  <div data-id="element-3917">
                    <h3 className="text-sm font-medium text-gray-700 mb-1" data-id="element-3918">
                      University
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-3919">
                      {request.university}
                    </p>
                  </div>
                  <div data-id="element-3920">
                    <h3 className="text-sm font-medium text-gray-700 mb-1" data-id="element-3921">
                      Field of Study
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-3922">
                      {request.fieldOfStudy}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3923">
              <div className="p-4 border-b border-gray-100 bg-gray-50" data-id="element-3924">
                <h2 className="text-lg font-semibold text-gray-800" data-id="element-3925">
                  Justification
                </h2>
              </div>
              <div className="p-4" data-id="element-3926">
                <p className="text-sm text-gray-800 whitespace-pre-line" data-id="element-3927">
                  {request.justification}
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3928">
              <div className="p-4 border-b border-gray-100 bg-gray-50" data-id="element-3929">
                <h2 className="text-lg font-semibold text-gray-800" data-id="element-3930">
                  Supporting Documents
                </h2>
              </div>
              <div className="p-4" data-id="element-3931">
                <ul className="space-y-3" data-id="element-3932">
                  {request.documents.map(doc => <li key={doc.id} className="flex items-center justify-between" data-id="element-3933">
                      <div className="flex items-center" data-id="element-3934">
                        <FileTextIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-3935" />
                        <span className="text-sm text-gray-800" data-id="element-3936">
                          {doc.name}
                        </span>
                      </div>
                      <button className="text-primary-600 hover:text-primary-700" data-id="element-3937">
                        <DownloadIcon className="w-4 h-4" data-id="element-3938" />
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
          {/* Right column - Messages */}
          <div className="lg:col-span-2" data-id="element-3939">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-full flex flex-col" data-id="element-3940">
              <div className="p-4 border-b border-gray-100 bg-gray-50" data-id="element-3941">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center" data-id="element-3942">
                  <MessageSquareIcon className="w-5 h-5 mr-2 text-primary-500" data-id="element-3943" />
                  Messages
                </h2>
              </div>
              <div className="flex-grow overflow-y-auto p-4 max-h-[500px]" data-id="element-3944">
                {messages.length > 0 ? <div className="space-y-4" data-id="element-3945">
                    {messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'student' ? 'justify-end' : 'justify-start'}`} data-id="element-3946">
                        <div className={`max-w-[80%] rounded-lg p-3 ${msg.sender === 'student' ? 'bg-primary-50 text-primary-900' : msg.sender === 'system' ? 'bg-gray-100 text-gray-800 border border-gray-200' : 'bg-blue-50 text-blue-900'}`} data-id="element-3947">
                          {msg.sender !== 'student' && <p className="text-xs font-medium mb-1" data-id="element-3948">
                              {msg.sender === 'system' ? 'System' : msg.sender}
                            </p>}
                          <p className="text-sm" data-id="element-3949">{msg.content}</p>
                          <p className="text-xs text-gray-500 mt-1 text-right" data-id="element-3950">
                            {msg.timestamp}
                          </p>
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-12" data-id="element-3951">
                    <MessageSquareIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-3952" />
                    <p className="text-gray-500" data-id="element-3953">No messages yet</p>
                  </div>}
              </div>
              <div className="p-4 border-t border-gray-100" data-id="element-3954">
                <form onSubmit={handleSendMessage} className="flex space-x-2" data-id="element-3955">
                  <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type a message..." className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-3956" />
                  <button type="button" className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-md" data-id="element-3957">
                    <PaperclipIcon className="w-5 h-5" data-id="element-3958" />
                  </button>
                  <button type="submit" disabled={!newMessage.trim()} className={`p-2 rounded-md ${newMessage.trim() ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-3959">
                    <SendIcon className="w-5 h-5" data-id="element-3960" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
};
export default ExemptionDetail;